// components/common/GoalChart.jsx

import React from 'react';
import { PencilIcon } from '@heroicons/react/24/outline';
import Button from '../common/Button';

export const GoalChart = ({
  currentValue,
  goalValue,
  formatValue,
  config,
  isEditing,
  onEditClick,
  onSave,
  onCancel,
  editValue,
  onEditValueChange,
  isSaving = false,
  inputType = "number",
  inputPlaceholder = "Enter goal",
  inputProps = {},
  markers = [],
  formatMarker = (val) => val,
}) => {
  const current = currentValue || 0;
  const goal = isEditing ? editValue : goalValue;
  const percentage = goal > 0 ? (current / goal) * 100 : 0;
  const surplus = Math.max(current - goal, 0);
  const remaining = Math.max(goal - current, 0);

  const isOverGoal = percentage > 100;
  const goalProportion = isOverGoal ? (goal / current) * 100 : percentage;
  const surplusProportion = isOverGoal ? (surplus / current) * 100 : 0;

  return (
    <div className="flex flex-col items-stretch">
      {/* Progress Bar - RESPONSIVE HEIGHT */}
      <div className="relative h-12 sm:h-14 flex-grow bg-gray-200 rounded-xl overflow-hidden">
        {/* Goal portion (Primary Color) */}
        <div
          className="absolute left-0 top-0 h-full transition-all duration-500 ease-out"
          style={{
            backgroundColor: config.colors.currentValue,
            width: `${isOverGoal ? goalProportion : percentage}%`,
            borderTopLeftRadius: "12px",
            borderBottomLeftRadius: "12px",
            borderTopRightRadius: isOverGoal ? "0" : "12px",
            borderBottomRightRadius: isOverGoal ? "0" : "12px"
          }}
        >
          {goalProportion >= 25 && (
            <span className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 text-white font-semibold text-xs sm:text-sm">
              {formatValue(current)}
            </span>
          )}
        </div>

        {/* Surplus (Yellow/Secondary Color) */}
        {isOverGoal && (
          <div
            className="absolute top-0 h-full transition-all duration-500 ease-out"
            style={{
              backgroundColor: config.colors.surplus,
              left: `${goalProportion}%`,
              width: `${surplusProportion}%`,
              borderTopRightRadius: "12px",
              borderBottomRightRadius: "12px"
            }}
          >
            {surplusProportion >= 25 && (
              <span className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-900 font-semibold text-xs sm:text-sm">
                +{formatValue(surplus)}
              </span>
            )}
          </div>
        )}

        {/* Remaining to Goal (Gray) */}
        {!isOverGoal && (
          <div
            className="absolute h-full"
            style={{
              backgroundColor: config.colors.remainingToGoal,
              left: `${percentage}%`,
              width: `${100 - percentage}%`,
              borderTopRightRadius: "12px",
              borderBottomRightRadius: "12px"
            }}
          >
            {(100 - percentage) >= 10 && (
              <span className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-700 font-semibold text-xs sm:text-sm">
                {formatValue(remaining)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Optional Markers (for months, milestones, etc.) */}
      {markers.length > 0 && (
        <div className="relative h-6 mt-1">
          {markers.map(({ value, position }) => (
            <div
              key={value}
              className="absolute transform -translate-x-1/2"
              style={{ left: `${position}%` }}
            >
              <div className="w-px h-3 bg-gray-400 mx-auto"></div>
              <div className="text-xs text-gray-600 mt-0.5 whitespace-nowrap">
                {formatMarker(value)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MOBILE LAYOUT (< 640px) - Compact inline with right-aligned edit */}
      <div className="flex sm:hidden items-center justify-between mt-3 gap-2">
        {/* Legend - LEFT SIDE */}
        <div className="flex flex-wrap gap-2 flex-1">
          {Object.keys(config.colors).map((key) => (
            <div key={key} className="flex items-center">
              <div 
                className="w-3 h-3 mr-1.5 rounded flex-shrink-0" 
                style={{ backgroundColor: config.colors[key] }}
              />
              <span className="text-xs text-gray-700 whitespace-nowrap">
                {config.text[key]}
              </span>
            </div>
          ))}
        </div>
        
        {/* Edit Controls - RIGHT SIDE */}
        {isEditing ? (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <input
              type={inputType}
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 w-20 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={inputPlaceholder}
              autoFocus
              {...inputProps}
            />
            <Button 
              variant="success"
              size="sm"
              iconOnly
              onClick={onSave}
              loading={isSaving}
              disabled={isSaving}
              title="Save"
            >
              ✓
            </Button>

            <Button 
              variant="secondary"
              size="sm"
              iconOnly
              onClick={onCancel}
              title="Cancel"
            >
              ✕
            </Button>
          </div>
        ) : (
          <div className="relative group flex-shrink-0">
            <Button 
              variant="ghost"
              size="sm"
              iconOnly
              leftIcon={<PencilIcon />}
              onClick={onEditClick}
              title={config.text.buttonName}
              className="relative"
            />
            
            {/* Tooltip on hover/touch */}
            <span className="absolute -top-10 right-0 bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity pointer-events-none z-10">
              {config.text.buttonName}
            </span>
          </div>
        )}
      </div>

      {/* DESKTOP LAYOUT (>= 640px) - Original full design */}
      <div className="hidden sm:flex sm:flex-row sm:justify-between sm:items-center mt-4 space-y-0">
        {/* Legend - CENTERED ON DESKTOP */}
        <div className="flex justify-center flex-grow space-x-6">
          {Object.keys(config.colors).map((key) => (
            <div key={key} className="flex items-center">
              <div 
                className="w-4 h-4 mr-2 rounded flex-shrink-0" 
                style={{ backgroundColor: config.colors[key] }}
              />
              <span className="text-sm text-gray-700 whitespace-nowrap">
                {config.text[key]}
              </span>
            </div>
          ))}
        </div>
        
        {/* Edit Button - DESKTOP DESIGN */}
        <div className="flex justify-end flex-shrink-0">
          {isEditing ? (
            <div className="flex space-x-2 items-center">
              <input
                type={inputType}
                value={editValue}
                onChange={(e) => onEditValueChange(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 w-32 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={inputPlaceholder}
                autoFocus
                {...inputProps}
              />
              <Button 
                variant="success"
                size="md"
                onClick={onSave}
                loading={isSaving}
                disabled={isSaving}
              >
                Save
              </Button>
              
              <Button 
                variant="secondary"
                size="md"
                onClick={onCancel}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button 
              variant="info"
              size="md"
              leftIcon={<PencilIcon />}
              onClick={onEditClick}
            >
              {config.text.buttonName}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};